import type { EsOfferPackage } from '@/types/earlySupporter';
import { SHOP_URL } from '@/constants/shop';

/**
 * Format a money amount (not a date) with the active locale: exactly 2 decimals, the euro sign
 * AFTER the number, separated by a NON-BREAKING SPACE U+00A0 ("29.00 €", DE "29,00 €"). Other currencies: "29.00 USD".
 */
export const formatEsMoney = (amount: number, currency: string, locale: string): string => {
  const code = currency.toUpperCase();
  let number: string;
  try {
    number = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  } catch {
    number = amount.toFixed(2);
  }
  return code === 'EUR' ? `${number}\u00A0€` : `${number}\u00A0${code}`;
};

/**
 * Per-period suffix ("/month") glued to the price: U+2060 WORD JOINER before the slash and after it,
 * so "29.00€/month" can never wrap apart when composed into a translated sentence.
 */
export const gluePriceSuffix = (suffix: string): string =>
  suffix === '' ? '' : `\u2060${suffix.replace(/^\//, '/\u2060')}`;

/** Storage is stored in MB (binary, 1024 per GB, same as the rest of the app); show GB with at most one decimal, no trailing .0. */
export const mbToGb = (mb: number): string => {
  const gb = Math.round((mb / 1024) * 10) / 10;
  return Number.isInteger(gb) ? String(gb) : gb.toFixed(1);
};

/** Package order: standalone first, then Brand Portfolios by number of Brand Spaces. */
export const sortEsPackages = (packages: EsOfferPackage[]): EsOfferPackage[] =>
  [...packages].sort((a, b) => {
    if (a.isAgency !== b.isAgency) return a.isAgency ? 1 : -1;
    return (a.maxWorkspaces ?? 0) - (b.maxWorkspaces ?? 0);
  });

/** "firstName lastName" from a full name (best effort prefill). */
export const splitFullName = (fullName: string | undefined | null): { firstName: string; lastName: string } => {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

/** Only same-origin absolute paths may be used as post-login return targets. */
export const isSafeInternalPath = (path: string): boolean =>
  path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') && !/[\u0000-\u001f\u007f]/.test(path);

/** Auth pages that must never be a post-login return target (would loop or log out again). */
const AUTH_PATH_PREFIXES = ['/login', '/logout', '/register', '/auth/'];

/** Post-login target: a safe internal path that is not itself an auth page, else null. */
export const getSafeReturnPath = (value: unknown): string | null => {
  if (typeof value !== 'string' || !isSafeInternalPath(value)) return null;
  const pathname = value.split(/[?#]/)[0].toLowerCase();
  const isAuthPage = AUTH_PATH_PREFIXES.some((p) => (p.endsWith('/') ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`)));
  return isAuthPage ? null : value;
};

const allowedPayHosts = (): string[] => {
  const hosts: string[] = [];
  try {
    hosts.push(new URL(SHOP_URL).hostname.toLowerCase());
  } catch {
    // SHOP_URL is a constant; ignore
  }
  const extra = (import.meta.env.VITE_ES_PAY_HOSTS ?? '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return [...hosts, ...extra];
};

/** A pay URL is only followed when it is https, on the shop host allow-list, and carries no userinfo. */
export const isSafePayUrl = (raw: unknown): raw is string => {
  if (typeof raw !== 'string') return false;
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:') return false;
    if (u.username || u.password) return false;
    return allowedPayHosts().includes(u.hostname.toLowerCase());
  } catch {
    return false;
  }
};

/** Billing tab for the account kind (Brand Portfolio accounts bill in the portfolio settings). */
/** Same return route the embedded checkout uses after a paid purchase; Billing then polls the checkout. */
export const esActivationPath = (checkoutId: string): string => {
  const params = new URLSearchParams({ tab: 'billing', purchase: 'success', checkout: checkoutId });
  return `/settings?${params.toString()}`;
};

export const esBillingPath = (kind: 'TEAM' | 'AGENCY' | null): string =>
  kind === 'AGENCY' ? '/agency/settings?tab=billing' : '/settings?tab=billing';

interface LocationLike {
  pathname: string;
  search: string;
}

const isSettingsPath = (pathname: string): boolean =>
  pathname === '/settings' || pathname.startsWith('/settings/') ||
  pathname === '/agency/settings' || pathname.startsWith('/agency/settings/');

/** Settings (team or Brand Portfolio) pages; Billing may redirect between the two after activation. */
export const isSettingsLocation = (location: LocationLike): boolean => isSettingsPath(location.pathname);

/**
 * True while the user is on a Shop purchase-return surface: the plan return page, or the
 * Billing settings page carrying `purchase=success` (with or without `checkout=`/`order=`).
 * The onboarding wizard must stay hidden (and Esc-to-skip ignored) here.
 */
export const isPurchaseReturnLocation = (location: LocationLike): boolean => {
  if (location.pathname.startsWith('/upgrade/early-supporter/return')) return true;
  if (!isSettingsPath(location.pathname)) return false;
  return new URLSearchParams(location.search).get('purchase') === 'success';
};
